import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Injectable, inject } from '@angular/core';
import { distinctUntilChanged, map, withLatestFrom } from 'rxjs/operators';
import { getRouterSelectors, routerNavigatedAction } from '@ngrx/router-store';
import { navOrdAutomaticShuffleStart, navOrdDropOffLiftOfTruckUrlStart, navOrdDropOffPlaceInStackUrlStart, navOrdPickUpReservedUrlStart, navOrdRelocateUrlStart, navParamOrderLineSeqNr, navParamOrderSequenceNumber, navParamOrderTransactionNr, navParamSeqNr } from 'src/app/shared/routing/routing.const';

import { AmqModeActions } from './amq-mode.actions';
import { AmqModeEnum } from 'src/app/shared/services/activeMQ/models/amq-mode.enum';
import { AmqMoveActions } from '../amq-move/amq-move.actions';
import { Store } from '@ngrx/store';

const {
    selectRouteParams,
    selectUrl,
} = getRouterSelectors();

@Injectable()
export class AmqModeEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);

    private readonly _checkForDropOffStart$ = createEffect(() =>
        this.actions$.pipe(
            ofType(routerNavigatedAction),
            withLatestFrom(
                this.store.select(selectUrl),
                this.store.select(selectRouteParams)
            ),
            map(([, url, params]) => {
                return url.startsWith(navOrdDropOffLiftOfTruckUrlStart) && !!params[navParamSeqNr];
            }
            ),
            distinctUntilChanged(),
            map(isOnPage => isOnPage ? AmqModeActions.onStartDropOffPage() : AmqModeActions.notOnStartDropOffPage())
        )
    );

    private readonly _checkForDropOffFinish$ = createEffect(() =>
        this.actions$.pipe(
            ofType(routerNavigatedAction),
            withLatestFrom(
                this.store.select(selectUrl),
                this.store.select(selectRouteParams)
            ),
            map(([, url, params]) => {
                return url.startsWith(navOrdDropOffPlaceInStackUrlStart) && !!params[navParamSeqNr];
            }
            ),
            distinctUntilChanged(),
            map(isOnPage => isOnPage ? AmqModeActions.onFinishDropOffPage() : AmqModeActions.notOnFinishDropOffPage())
        )
    );

    private readonly _checkForPickUpPage$ = createEffect(() =>
        this.actions$.pipe(
            ofType(routerNavigatedAction),
            withLatestFrom(
                this.store.select(selectUrl),
                this.store.select(selectRouteParams)
            ),
            map(([, url, params]) => {
                return url.startsWith(navOrdPickUpReservedUrlStart) && !!params[navParamOrderTransactionNr];
            }
            ),
            distinctUntilChanged(),
            map(isOnPage => isOnPage ? AmqModeActions.onPickUpPage() : AmqModeActions.notOnPickUpPage())
        )
    );

    private readonly _checkForRelocateOrderPage$ = createEffect(() =>
        this.actions$.pipe(
            ofType(routerNavigatedAction),
            withLatestFrom(
                this.store.select(selectUrl),
                this.store.select(selectRouteParams)
            ),
            map(([, url, params]) => {
                return url.startsWith(navOrdRelocateUrlStart) && !!params[navParamOrderSequenceNumber] && !!params[navParamOrderLineSeqNr];
            }
            ),
            distinctUntilChanged(),
            map(isOnPage => isOnPage ? AmqModeActions.onRelocatePage() : AmqModeActions.notOnRelocatePage())
        )
    );

    private readonly _checkForStandardRelocatePage$ = createEffect(() =>
        this.actions$.pipe(
            ofType(routerNavigatedAction),
            withLatestFrom(
                this.store.select(selectUrl),
            ),
            map(([, url]) => {
                return url.startsWith(navOrdAutomaticShuffleStart);
            }),
            distinctUntilChanged(),
            map(isOnPage => isOnPage ? AmqModeActions.onStandardRelocatePage() : AmqModeActions.notOnStandardRelocatePage())
        )
    );

    private readonly _checkIfDropOffHasStarted$ = createEffect(() =>
        this.actions$.pipe(
            ofType(routerNavigatedAction),
            withLatestFrom(
                this.store.select(selectUrl),
                this.store.select(selectRouteParams)
            ),
            map(([, url, params]) => {
                return url.startsWith(navOrdDropOffPlaceInStackUrlStart) && !!params[navParamSeqNr];
            }
            ),
            distinctUntilChanged(),
            map(isOnPage => isOnPage ? AmqMoveActions.setMoveInProgressBySystem({ mode: AmqModeEnum.dropOff }) : AmqModeActions.noAction())
        )
    );

    private readonly _checkIfPickUpHasStarted$ = createEffect(() =>
        this.actions$.pipe(
            ofType(routerNavigatedAction),
            withLatestFrom(
                this.store.select(selectUrl),
                this.store.select(selectRouteParams)
            ),
            map(([, url, params]) => {
                return url.startsWith(navOrdPickUpReservedUrlStart) && !!params[navParamOrderTransactionNr];
            }
            ),
            distinctUntilChanged(),
            map(isOnPage => isOnPage ? AmqMoveActions.setMoveInProgressBySystem({ mode: AmqModeEnum.pickUp }) : AmqModeActions.noAction())
        )
    );

    private readonly _checkIfAutomaticShuffleHasStarted$ = createEffect(() =>
        this.actions$.pipe(
            ofType(routerNavigatedAction),
            withLatestFrom(
                this.store.select(selectUrl),
                this.store.select(selectRouteParams)
            ),
            map(([, url, params]) => {
                return url.startsWith(navOrdAutomaticShuffleStart);
            }
            ),
            distinctUntilChanged(),
            map(isOnPage => isOnPage ? AmqMoveActions.setMoveInProgressBySystem({ mode: AmqModeEnum.standard }) : AmqModeActions.noAction())
        )
    );

    private readonly _checkIfRelocateHasStarted$ = createEffect(() =>
        this.actions$.pipe(
            ofType(routerNavigatedAction),
            withLatestFrom(
                this.store.select(selectUrl),
                this.store.select(selectRouteParams)
            ),
            map(([, url, params]) => {
                return url.startsWith(navOrdRelocateUrlStart);
            }
            ),
            distinctUntilChanged(),
            map(isOnPage => isOnPage ? AmqMoveActions.setMoveInProgressBySystem({ mode: AmqModeEnum.relocateOrder }) : AmqModeActions.noAction())
        )
    );
}
